// src/components/trial/lp/trial-beta/components/trial-modal.tsx
// Ported from the source's trial-modal.js (7278-7350) and trial-modal.css (2252-2296). The source
// builds the modal by `cloneNode(true)`-ing the hero card (`#hero-form [data-anim="hero-form-card"]`)
// into a detached `#np-trial-modal` appended to `document.body`. In React this instead renders a
// SECOND `<SignupForm />` — same title/subtitle/certified-by chrome as the hero card, reusing the
// same `hero.*` translation keys since it is visually the same card — with no DOM cloning.
//
// This component is rendered as a normal child of `<main className='trial-beta-page'>`
// (`index.tsx`), NOT portaled to `document.body`: every selector in trial-beta.css's modal block
// is scoped `.trial-beta-page #np-trial-modal ...` (the single deliberate exception being the
// scroll-lock class below, which has to live on <body> because `overflow:hidden` on an inner div
// does not stop page scroll) — a portal to `document.body` would escape that ancestor and none of
// the scoped rules would ever match.
import { useTranslation } from 'next-i18next';
import { useEffect, useRef } from 'react';

import { CertifiedBy } from '@/components/trial/lp/trial-beta/components/certified-by';
import { SignupForm } from '@/components/trial/lp/trial-beta/components/signup-form';

interface TrialModalProps {
  isOpen: boolean;
  onClose: () => void;
  daysTrial: number;
  trialConfigId: number | null;
  trialPageId: number | null;
  isDisabledButton?: boolean;
}

// Mirrors the source's `focusable()` helper (trial-modal.js:7305) verbatim, including the
// `offsetParent !== null` visibility filter.
const FOCUSABLE_SELECTOR = 'button:not([disabled]),input:not([disabled]),a[href],[tabindex]:not([tabindex="-1"])';

export function TrialModal({ isOpen, onClose, daysTrial, trialConfigId, trialPageId, isDisabledButton }: TrialModalProps) {
  const { t } = useTranslation('components/trial/lp/trial-beta');
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<Element | null>(null);

  // Scroll lock + open/close focus handling. Split from the Escape/Tab listener below because
  // this one only needs to run once per open/close transition, not on every keydown.
  useEffect(() => {
    if (!isOpen) return undefined;

    previousFocusRef.current = document.activeElement;
    // Deliberate exception to the scoping rule (see file header + trial-beta.css comment): the
    // scroll lock has to land on <body>, unscoped, because `overflow:hidden` on an inner div
    // does not stop page scroll.
    document.body.classList.add('np-trial-modal-open');

    // Mirrors the source's 230ms post-transition focus handoff to the first field.
    const focusTimer = window.setTimeout(() => {
      dialogRef.current?.querySelector<HTMLElement>('input')?.focus({ preventScroll: true });
    }, 230);

    return () => {
      window.clearTimeout(focusTimer);
      // Removed unconditionally on cleanup — including on unmount — so an unmount mid-open can
      // never leave the page permanently unscrollable.
      document.body.classList.remove('np-trial-modal-open');

      const previousFocus = previousFocusRef.current;
      if (previousFocus instanceof HTMLElement && document.contains(previousFocus)) {
        previousFocus.focus({ preventScroll: true });
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusableElements = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((focusableElement) => focusableElement.offsetParent !== null);
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (!firstElement || !lastElement) return;

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div id='np-trial-modal' className='is-open' data-np-modal-surface=''>
      <div className='np-trial-modal__backdrop' onClick={onClose} />
      <div ref={dialogRef} className='np-trial-modal__dialog' role='dialog' aria-modal='true' aria-labelledby='np-trial-modal-title'>
        <button type='button' className='np-trial-modal__close' onClick={onClose} aria-label={t('modal.close')}>
          <svg viewBox='0 0 24 24' aria-hidden='true'>
            <path d='M6 6l12 12M18 6L6 18' />
          </svg>
        </button>
        <div className='np-trial-modal__scroll'>
          <div className='np-trial-modal__mount'>
            <div className='np-trial-modal__card relative flex flex-1 flex-col justify-center rounded-xl border p-6 md:p-8'>
              <div className='pointer-events-none absolute inset-0' style={{ background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(46,205,255,0.02) 2px, rgba(46,205,255,0.02) 4px)' }} aria-hidden='true' />
              <div className='relative z-10 flex flex-1 flex-col'>
                <h2 id='np-trial-modal-title' className='mb-2 text-center text-2xl font-bold md:text-3xl' style={{ fontFamily: 'var(--font-bai)', color: '#E5E7EB' }}>
                  {t('hero.formTitle')}
                </h2>
                <p className='mb-5 text-center text-sm' style={{ color: '#929298', fontFamily: 'var(--font-outfit)' }}>
                  {t('hero.subtitle', { count: daysTrial })}
                </p>
                <SignupForm daysTrial={daysTrial} trialConfigId={trialConfigId} trialPageId={trialPageId} isDisabledButton={isDisabledButton} formId='v2b-modal-form' variant='modal' />
                <CertifiedBy />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
